// Session PJ2: the OFFICE's view of a project site survey, the pure half. Words for a status, a recorded value and who recorded it,
// which photo slots are plates, and the conversion between what an editor holds and a cell. No React, no I/O. The panel and the
// section only call these.
//
// Nothing here is a price, and nothing here names a customer contact.
import type { ProjectSurveyPlateProposal } from "../../../lib/api";
import { formatDateTime } from "../../../lib/format";
import { NO_ACCESS_REASONS, type SurveyCell, type SurveyValue } from "../../../lib/surveyRecord";
import { SURVEY_TYPES, type ChipOption, type SurveyField } from "../../../lib/surveyTemplate";

// A survey's status words and tones live in the one status registry (src/lib/statusRegistry.ts, domain "projectSurvey", Session PJ2F2).

export function surveyTypeWords(type: string | null | undefined): string {
  return type && Object.prototype.hasOwnProperty.call(SURVEY_TYPES, type) ? SURVEY_TYPES[type as keyof typeof SURVEY_TYPES] : "Type not chosen";
}

/** The visit statuses the engineer can still write through (the server's OPEN_VISIT_STATUSES). */
export const OPEN_VISIT = ["scheduled", "in_progress", "parts_pending"] as const;
export const isOpenVisit = (status: string): boolean => (OPEN_VISIT as readonly string[]).includes(status);

// ---- Values -----------------------------------------------------------------------------------------------------------

/** What an editor needs to know about a value: a field of a section, or a column of a table. */
export interface ValueSpec {
  kind: SurveyField["kind"] | "mm" | "number" | "chips_single" | "chips_multi" | "text";
  options?: readonly ChipOption[];
  unit?: string;
  allowOther?: boolean;
}

export function specOfField(field: SurveyField): ValueSpec {
  return { kind: field.kind, options: field.options, unit: field.unit, allowOther: field.allowOther };
}

const YES_NO_WORDS: Record<string, string> = { yes: "Yes", no: "No", unknown: "Not known" };

/** A recorded value in the office's words. "" when there is none. `otherWords` are the words kept in the section notes for an Other chip. */
export function valueWords(spec: ValueSpec, value: SurveyValue | undefined, otherWords?: string): string {
  if (value === undefined || value === null) return "";
  const optionLabel = (v: string): string => {
    const hit = spec.options?.find((o) => o.value === v);
    if (hit) return hit.label;
    if (v === "other") return otherWords ? `Other: ${otherWords}` : "Other";
    // A typed answer to an Other choice is stored as the words themselves.
    return spec.allowOther ? `Other: ${v}` : v;
  };
  switch (spec.kind) {
    case "yes_no":
    case "yes_no_unknown":
      return typeof value === "string" ? (YES_NO_WORDS[value] ?? value) : String(value);
    case "chips_single":
      return typeof value === "string" ? optionLabel(value) : String(value);
    case "chips_multi":
      return Array.isArray(value) ? value.map(optionLabel).join(", ") : String(value);
    case "mm":
      return `${value} mm`;
    case "number":
      return spec.unit ? `${value} ${spec.unit}` : String(value);
    case "datetime":
      return typeof value === "string" ? formatDateTime(value) : String(value);
    default:
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

export function noAccessWords(na: NonNullable<SurveyCell["na"]>): string {
  return `Could not access: ${NO_ACCESS_REASONS[na.reason] ?? na.reason}${na.note ? `, ${na.note}` : ""}`;
}

/** Who recorded a value, in words the office reads. */
export function recordedByWords(cell: SurveyCell | undefined): string | null {
  if (!cell) return null;
  if (cell.src === "photo_confirmed") return "Read from photo, confirmed by the office";
  if (cell.by === "office") return "Corrected by the office";
  return "Recorded by the engineer";
}

// ---- Editors <-> cells ------------------------------------------------------------------------------------------------

/** "2026-10-27T09:30" in the browser's zone for a datetime-local box, from an ISO instant. "" when it cannot be read. */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function fromLocalInput(local: string): string | null {
  const d = new Date(local);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

/** A number typed into a box: whole or with a decimal point, or null when it is not one. */
export function parseTypedNumber(raw: string): number | null {
  const s = raw.trim().replace(",", ".");
  if (!/^-?\d+(?:\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// ---- Plates -----------------------------------------------------------------------------------------------------------

/** The slot a stored photo key belongs to: "landing_opening_photo.f2" -> "landing_opening_photo". */
export function slotBase(slotKey: string): string {
  return slotKey.split(".")[0] ?? slotKey;
}

const PLATE_SLOTS = new Set(["controller_label_photo", "drive_label_photo", "governor_photo"]);

/** Whether the office can ask for a plate photo to be read: a data plate or a label. */
export function isPlateSlot(slotKey: string): boolean {
  const base = slotBase(slotKey);
  return base.endsWith("_plate_photo") || PLATE_SLOTS.has(base);
}

/** A plate proposal as the cell the office would save: a number for a measured field, the text otherwise. Null when it cannot be saved. */
export function proposalValue(field: SurveyField | null, proposal: ProjectSurveyPlateProposal): SurveyValue | null {
  if (!field) return null;
  if (field.kind === "mm" || field.kind === "number") {
    const n = typeof proposal.value === "number" ? proposal.value : parseTypedNumber(String(proposal.value));
    return n;
  }
  if (field.kind === "chips_single" || field.kind === "text") return String(proposal.value);
  return null;
}
